import { Card, Typography, Descriptions, Button, Space, message, Alert, Image, Tag } from 'antd';
import { CheckCircleIcon, WarningCircleIcon } from '@phosphor-icons/react';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import {
  clearStylingWorkflowPayload,
  setIsStylingWorkflowDrawerOpen,
} from '../../../store/stylist/slice';
import {
  useCreateStylingWorkflowMutation,
  useGetOccasionsQuery,
  useStartWorkflowRunMutation,
} from '../../../store/stylist/stylistApi';
import { StylingWorkflowCreateDTOType } from '../../../schemas/styling';

const { Title, Text } = Typography;

type ReviewAndCreateProps = {
  workflowGenre?: 'standard' | 'occasion';
};

const ReviewAndCreate = ({ workflowGenre = 'standard' }: ReviewAndCreateProps) => {
  const dispatch = useAppDispatch();
  const workflowPayload = useAppSelector(state => state.stylist.stylingWorkflowCreatePayload);
  const { data: occasionTree } = useGetOccasionsQuery();

  const [createWorkflow, { isLoading: isCreating }] = useCreateStylingWorkflowMutation();
  const [startWorkflowRun, { isLoading: isStarting }] = useStartWorkflowRunMutation();

  const isLoading = isCreating || isStarting;

  const itemsHaveRequiredFields =
    workflowPayload?.items?.every(item => Boolean(item.base_product_color)) ?? false;

  const isValid =
    workflowPayload?.name &&
    workflowPayload?.items &&
    workflowPayload.items.length > 0 &&
    itemsHaveRequiredFields &&
    (workflowGenre !== 'occasion'
      ? workflowPayload?.prompt_template
      : workflowPayload?.prompt_template &&
        workflowPayload?.image_prompt_template &&
        workflowPayload?.occasion_id &&
        workflowPayload?.sub_occasion_id);

  const selectedOccasion = (occasionTree ?? []).find(
    occasion => occasion.id === workflowPayload?.occasion_id,
  );
  const selectedSubOccasion = (selectedOccasion?.children ?? []).find(
    subOccasion => subOccasion.id === workflowPayload?.sub_occasion_id,
  );

  const handleCreate = async () => {
    if (!workflowPayload || !isValid) {
      message.error('Please fill in all required fields');
      return;
    }

    try {
      // Step 1: Create the workflow (without items)
      const workflowCreateData: StylingWorkflowCreateDTOType = {
        name: workflowPayload.name,
        description: workflowPayload.description,
        prompt_template: workflowPayload.prompt_template,
        image_prompt_template: workflowPayload.image_prompt_template,
        workflow_genre: workflowPayload.workflow_genre,
        occasion_id: workflowPayload.occasion_id,
        sub_occasion_id: workflowPayload.sub_occasion_id,
        instructions: workflowPayload.instructions,
      };

      const createdWorkflow = await createWorkflow(workflowCreateData).unwrap();
      message.success('Styling workflow created successfully!');

      // Step 2: Start the workflow run with the items (now using correct field names)
      await startWorkflowRun({
        workflowId: createdWorkflow.id,
        items: workflowPayload.items || [],
      }).unwrap();
      message.success('Workflow started successfully!');

      // Clean up and close
      dispatch(clearStylingWorkflowPayload());
      dispatch(setIsStylingWorkflowDrawerOpen(false));
    } catch (error) {
      console.error('Failed to create/start workflow:', error);
      message.error('Failed to create or start workflow. Please try again.');
    }
  };

  const validationIssues = [];
  if (!workflowPayload?.name) validationIssues.push('Workflow name is required');
  if (workflowGenre === 'standard' && !workflowPayload?.prompt_template) {
    validationIssues.push('Prompt template is required');
  }
  if (!workflowPayload?.items || workflowPayload.items.length === 0) {
    validationIssues.push('At least one product is required');
  }
  if (!itemsHaveRequiredFields) {
    validationIssues.push('Each product must include a color');
  }
  if (workflowGenre === 'occasion') {
    if (!workflowPayload?.prompt_template) {
      validationIssues.push('Base prompt template is required');
    }
    if (!workflowPayload?.image_prompt_template) {
      validationIssues.push('Image prompt template is required');
    }
    if (!workflowPayload?.occasion_id) {
      validationIssues.push('Occasion is required');
    }
    if (!workflowPayload?.sub_occasion_id) {
      validationIssues.push('Sub-Occasion is required');
    }
  }

  return (
    <Card style={{ margin: '16px 0' }}>
      <Title level={4}>Review & Create</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Review your workflow configuration. Once created, the workflow will automatically start
        processing your selected products.
      </Text>

      {validationIssues.length > 0 && (
        <Alert
          message="Please complete the following:"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {validationIssues.map((issue, index) => (
                <li key={index}>{issue}</li>
              ))}
            </ul>
          }
          type="warning"
          icon={<WarningCircleIcon />}
          style={{ marginBottom: 24 }}
        />
      )}

      {isValid && (
        <Alert
          message="Workflow is ready to create and start!"
          description="All required fields are completed. The workflow will automatically begin processing once created."
          type="success"
          icon={<CheckCircleIcon />}
          style={{ marginBottom: 24 }}
        />
      )}

      <Descriptions title="Workflow Summary" bordered column={1}>
        <Descriptions.Item label="Workflow Name">{workflowPayload?.name || '-'}</Descriptions.Item>
        <Descriptions.Item label="Description">
          {workflowPayload?.description || 'No description provided'}
        </Descriptions.Item>
        {workflowGenre === 'standard' && (
          <Descriptions.Item label="Prompt Template ID">
            {workflowPayload?.prompt_template || '-'}
          </Descriptions.Item>
        )}
        {workflowGenre === 'occasion' && (
          <>
            <Descriptions.Item label="Base Prompt Template ID">
              {workflowPayload?.prompt_template || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Image Prompt Template ID">
              {workflowPayload?.image_prompt_template || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Occasion">{selectedOccasion?.name || '-'}</Descriptions.Item>
            <Descriptions.Item label="Sub-Occasion">
              {selectedSubOccasion?.name || '-'}
            </Descriptions.Item>
          </>
        )}
        <Descriptions.Item label="Number of Products">
          {workflowPayload?.items?.length || 0}
        </Descriptions.Item>
      </Descriptions>

      {workflowPayload?.items && workflowPayload.items.length > 0 && (
        <>
          <Title level={5} style={{ marginTop: 24, marginBottom: 16 }}>
            Product Preview
          </Title>
          <Space direction="vertical" style={{ width: '100%' }}>
            {workflowPayload.items.slice(0, 3).map((item, index) => (
              <Card key={index} size="small" style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <Image
                    width={40}
                    height={40}
                    src={item.source_image_url}
                    style={{ objectFit: 'cover', borderRadius: 4 }}
                  />
                  <div style={{ flex: 1 }}>
                    <div>
                      <strong>Product ID:</strong> {item.base_product_id}
                    </div>
                    <div>
                      <Tag color="blue">{item.base_product_type}</Tag>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
            {workflowPayload.items.length > 3 && (
              <Text type="secondary">... and {workflowPayload.items.length - 3} more products</Text>
            )}
          </Space>
        </>
      )}

      <div style={{ marginTop: 24, textAlign: 'center' }}>
        <Button
          type="primary"
          size="large"
          onClick={handleCreate}
          loading={isLoading}
          disabled={!isValid}
        >
          {isCreating
            ? 'Creating Workflow...'
            : isStarting
              ? 'Starting Workflow...'
              : 'Create & Start Workflow'}
        </Button>
      </div>
    </Card>
  );
};

export default ReviewAndCreate;
